/**
 * Artists index: `artist` metaobjects via Storefront API, client-side filters and load-more (16 per page).
 */
(function () {
  var QUERY = [
    'query ArtistsIndex($type: String!, $first: Int!, $after: String) {',
    '  metaobjects(type: $type, first: $first, after: $after) {',
    '    pageInfo { hasNextPage endCursor }',
    '    edges {',
    '      node {',
    '        handle',
    '        name: field(key: "name") { value }',
    '        nationality: field(key: "nationality") { value }',
    '        category: field(key: "category") { value }',
    '        medium: field(key: "medium") { value }',
    '        exhibition: field(key: "exhibition") { value }',
    '        works_available: field(key: "works_available") { value }',
    '        portrait: field(key: "portrait") {',
    '          value',
    '          reference {',
    '            __typename',
    '            ... on MediaImage {',
    '              image { url(transform: { maxWidth: 900 }) altText }',
    '            }',
    '          }',
    '        }',
    '        secondary_hover_image: field(key: "secondary_hover_image") {',
    '          value',
    '          reference {',
    '            __typename',
    '            ... on MediaImage {',
    '              image { url(transform: { maxWidth: 900 }) altText }',
    '            }',
    '          }',
    '        }',
    '        artists_page: field(key: "artists_page") {',
    '          value',
    '          reference {',
    '            __typename',
    '            ... on Collection { handle onlineStoreUrl }',
    '          }',
    '        }',
    '        collection: field(key: "collection") {',
    '          value',
    '          reference {',
    '            __typename',
    '            ... on Collection { handle onlineStoreUrl }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function parseList(val) {
    if (val == null || val === '') return [];
    if (Array.isArray(val)) return val.map(String).map(function (s) { return s.trim(); }).filter(Boolean);
    var s = String(val).trim();
    try {
      var j = JSON.parse(s);
      if (Array.isArray(j)) return j.map(String).map(function (x) { return x.trim(); }).filter(Boolean);
      if (j != null) return [String(j)];
    } catch (e) {
      /* fall through */
    }
    return s.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function parseBool(val) {
    if (val === true || val === false) return val;
    var s = String(val == null ? '' : val).toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'yes';
  }

  function imageFromField(field) {
    if (!field) return '';
    var ref = field.reference;
    if (ref && ref.image && ref.image.url) return ref.image.url;
    var v = field.value;
    if (v && /^https?:\/\//i.test(String(v))) return String(v);
    return '';
  }

  function imageAltFromField(field, fallback) {
    if (field && field.reference && field.reference.image && field.reference.image.altText) {
      return field.reference.image.altText;
    }
    return fallback || '';
  }

  function collectionUrlFromField(field) {
    if (!field) return '';
    var ref = field.reference;
    if (ref && ref.__typename === 'Collection') {
      if (ref.onlineStoreUrl) return ref.onlineStoreUrl;
      if (ref.handle) return '/collections/' + ref.handle;
    }
    var v = field.value;
    if (v) {
      v = String(v).trim();
      if (/^https?:\/\//i.test(v) || v.indexOf('/') === 0) return v;
    }
    return '';
  }

  function artistPageUrlFromFields(artistsPageField, collectionField) {
    var url = collectionUrlFromField(artistsPageField);
    if (url) return url;
    return collectionUrlFromField(collectionField);
  }

  function collectionHandleFromField(field) {
    if (!field) return '';
    var ref = field.reference;
    if (ref && ref.__typename === 'Collection' && ref.handle) return ref.handle;
    return '';
  }

  function lookupArtistUrlFromMap(handle, artistUrlMap) {
    if (!artistUrlMap || !handle) return '';
    if (artistUrlMap[handle]) return artistUrlMap[handle];
    var normalized = String(handle).toLowerCase();
    var keys = Object.keys(artistUrlMap);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].toLowerCase() === normalized) return artistUrlMap[keys[i]];
    }
    return '';
  }

  function resolveArtistPageUrl(node, artistUrlMap) {
    var url = artistPageUrlFromFields(node.artists_page, node.collection);
    if (url) return url;
    url = lookupArtistUrlFromMap(node.handle, artistUrlMap);
    if (url) return url;
    var collectionHandle =
      collectionHandleFromField(node.artists_page) || collectionHandleFromField(node.collection);
    if (collectionHandle) return '/collections/' + collectionHandle;
    if (node.handle) return '/collections/' + node.handle;
    return '#';
  }

  function escAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  function firstLetterBucket(name) {
    var n = String(name || '').trim();
    if (!n) return '#';
    var c = n.charAt(0).toUpperCase();
    if (c >= 'A' && c <= 'Z') return c;
    return '#';
  }

  function normalizeArtist(node, artistUrlMap) {
    var nameField = node.name;
    var name = nameField && nameField.value != null ? String(nameField.value).trim() : node.handle || 'Artist';
    var portraitUrl = imageFromField(node.portrait);
    var hoverUrl = imageFromField(node.secondary_hover_image);
    var natVal = node.nationality && node.nationality.value;
    var nationality = natVal != null && String(natVal).trim() !== '' ? parseList(natVal) : [];
    if (nationality.length === 0 && natVal) nationality = [String(natVal).trim()];

    return {
      handle: node.handle,
      name: name,
      portraitUrl: portraitUrl,
      portraitAlt: imageAltFromField(node.portrait, name) || name,
      hoverUrl: hoverUrl,
      hoverAlt: imageAltFromField(node.secondary_hover_image, name) || name,
      collectionUrl: resolveArtistPageUrl(node, artistUrlMap),
      nationality: nationality,
      category: parseList(node.category && node.category.value),
      medium: parseList(node.medium && node.medium.value),
      exhibition: parseBool(node.exhibition && node.exhibition.value),
      works_available: parseBool(node.works_available && node.works_available.value),
      letter: firstLetterBucket(name),
    };
  }

  function getConnectionPayload(data) {
    var mo = data && data.data && data.data.metaobjects;
    if (!mo) return { nodes: [], pageInfo: {} };
    if (mo.edges && mo.edges.length) {
      return {
        nodes: mo.edges.map(function (e) { return e.node; }),
        pageInfo: mo.pageInfo || {},
      };
    }
    if (mo.nodes && mo.nodes.length) {
      return { nodes: mo.nodes, pageInfo: mo.pageInfo || {} };
    }
    return { nodes: [], pageInfo: mo.pageInfo || {} };
  }

  function uniqSort(arr) {
    var o = {};
    arr.forEach(function (x) {
      if (x) o[String(x).trim()] = true;
    });
    return Object.keys(o).sort(function (a, b) { return a.localeCompare(b); });
  }

  window.initArtistsIndex = function (sectionEl) {
    if (!sectionEl) return;
    var app = sectionEl.querySelector('[data-artists-index-root]');
    if (!app) return;
    if (app._artistsAbort) app._artistsAbort.abort();
    app._artistsAbort = new AbortController();
    var signal = app._artistsAbort.signal;

    var apiUrl = app.dataset.apiUrl;
    var token = app.dataset.token;
    var metaType = app.dataset.metaobjectType || 'artist';
    var artistUrlMap = {};
    var artistUrlsEl = app.querySelector('[data-artist-page-urls]');
    if (artistUrlsEl) {
      try {
        artistUrlMap = JSON.parse(artistUrlsEl.textContent) || {};
      } catch (e) {
        artistUrlMap = {};
      }
    }

    var loadingEl = app.querySelector('[data-artists-loading]');
    var errorEl = app.querySelector('[data-artists-error]');
    var mainEl = app.querySelector('[data-artists-main]');
    var gridEl = app.querySelector('[data-artists-grid]');
    var paginationWrapEl = app.querySelector('[data-artists-pagination-wrap]');
    var paginationCountEl = app.querySelector('[data-artists-pagination-count]');
    var loadMoreWrapEl = app.querySelector('[data-artists-load-more-wrap]');
    var loadMoreBtn = app.querySelector('[data-artists-load-more]');
    var pageSize = parseInt(app.dataset.pageSize, 10) || 16;
    if (pageSize < 1) pageSize = 16;
    var viewingTemplate = app.dataset.i18nViewing || "You're viewing {{ of }} of {{ total }}";
    var countEl = app.querySelector('[data-results-count]');
    var lettersEl = app.querySelector('[data-filter-letters]');
    var sidebarForm = app.querySelector('[data-artists-filter-form]');
    var filterBarForm = app.querySelector('[data-artists-filter-bar]');
    var filterSidebarInner = sidebarForm ? sidebarForm.querySelector('.filter-sidebar-inner') : null;
    var sectionIdEl = sectionEl.querySelector('[data-section-id]');
    var sectionId = sectionIdEl
      ? sectionIdEl.getAttribute('data-section-id')
      : sectionEl.id.replace('shopify-section-', '');
    var filterDrawerEl = document.querySelector(
      '[data-artists-filter-drawer][data-section-id="' + sectionId + '"]'
    );
    if (filterDrawerEl && filterDrawerEl.parentNode !== document.body) {
      document.body.appendChild(filterDrawerEl);
    }
    var drawerFiltersInner = filterDrawerEl
      ? filterDrawerEl.querySelector('[data-artists-drawer-filters-inner]')
      : null;
    var drawerResultsCountEl = filterDrawerEl
      ? filterDrawerEl.querySelector('[data-artists-drawer-results-count]')
      : null;
    var sidebarToggleBtn = app.querySelector('[data-artists-sidebar-toggle]');
    var sortToggleBtn = app.querySelector('[data-artists-sort-toggle]');
    var sortDropdownEl = app.querySelector('[data-artists-sort-dropdown]');
    var sortLabelEl = app.querySelector('[data-sort-label]');
    var mqMobile = window.matchMedia('(max-width: 719px)');

    var catListEl = app.querySelector('[data-filter-category-list]');
    var medListEl = app.querySelector('[data-filter-medium-list]');
    var natListEl = app.querySelector('[data-filter-nationality-list]');
    var exEl = app.querySelector('[data-filter-exhibition]');
    var worksEl = app.querySelector('[data-filter-works]');
    var clearBtn = app.querySelector('[data-filter-clear]');
    var idSlug = String(sectionEl.id || 'artists').replace(/^shopify-section-/, '').replace(/[^a-zA-Z0-9_-]/g, '');
    var drawerCloseTimer = null;
    var DRAWER_TRANSITION_MS = 500;

    function shouldAnimateDrawer() {
      return (
        sectionEl.classList.contains('animation') &&
        !document.documentElement.classList.contains('prefers-reduced-motion')
      );
    }

    var allArtists = [];
    var filtered = [];
    var activeLetter = 'all';
    var visibleCount = pageSize;

    function showError(msg) {
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
      if (mainEl) mainEl.hidden = true;
    }

    function showMain() {
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (mainEl) mainEl.hidden = false;
      document.documentElement.style.setProperty('--sticky-filter-bar-height', '0px');
    }

    function apiFetch(variables) {
      return fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: QUERY, variables: variables }),
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw new Error(j && j.message ? j.message : 'HTTP ' + r.status);
          if (j.errors && j.errors.length) throw new Error(j.errors.map(function (e) { return e.message; }).join('; '));
          return j;
        });
      });
    }

    function loadAll() {
      var acc = [];
      var after = null;

      function next() {
        return apiFetch({ type: metaType, first: 100, after: after }).then(function (json) {
          var payload = getConnectionPayload(json);
          payload.nodes.forEach(function (node) {
            acc.push(normalizeArtist(node, artistUrlMap));
          });
          var pi = payload.pageInfo;
          if (pi.hasNextPage && pi.endCursor) {
            after = pi.endCursor;
            return next();
          }
          return acc;
        });
      }

      return next();
    }

    function facetCounts(artists, key) {
      var map = {};
      artists.forEach(function (a) {
        var vals = a[key];
        if (!vals || !vals.length) return;
        vals.forEach(function (v) {
          var k = String(v).trim();
          if (!k) return;
          map[k] = (map[k] || 0) + 1;
        });
      });
      return map;
    }

    function facetRowHtml(facet, value, count, idx) {
      var id = 'artists-facet-' + facet + '-' + idSlug + '-' + idx;
      var dis = count === 0;
      return (
        '<li class="filter-item">' +
        '<label data-filter-item class="filter-item__content' +
        (dis ? ' filter-item__content--disabled' : '') +
        '" for="' +
        id +
        '">' +
        '<input type="checkbox" class="filter-item__checkbox visually-hidden" data-filter-item-input data-artists-facet="' +
        facet +
        '" value="' +
        escAttr(value) +
        '" id="' +
        id +
        '"' +
        (dis ? ' disabled' : '') +
        '>' +
        '<div class="filter-item__content-inner">' +
        '<div class="filter-input__input">' +
        '<span class="icon icon-new icon-checkmark">' +
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1.88 11.988 6.21 6.103L22.467 3.83" stroke="currentColor" stroke-width="3.055"></path></svg>' +
        '</span>' +
        '</div>' +
        '<span class="filter-item__label">' +
        escHtml(value) +
        '</span>' +
        '<span class="filter-item__count fs-body-75">(' +
        count +
        ')</span>' +
        '</div>' +
        '</label>' +
        '</li>'
      );
    }

    function fillFacetList(ul, facet, values, artists) {
      if (!ul) return;
      var counts = facetCounts(artists, facet);
      ul.innerHTML = values
        .map(function (v, idx) {
          return facetRowHtml(facet, v, counts[v] || 0, idx);
        })
        .join('');
    }

    function selectedFacetValues(facet) {
      return Array.prototype.map.call(
        app.querySelectorAll('input[data-artists-facet="' + facet + '"]:checked:not(:disabled)'),
        function (inp) {
          return inp.value;
        }
      );
    }

    function getSortValue() {
      var inp = sectionEl.querySelector('input[name^="artists_sort_by"]:checked');
      return inp ? inp.value : 'most-relevant';
    }

    function updateSortLabel() {
      if (!sortLabelEl) return;
      var prefix = sortLabelEl.getAttribute('data-sort-title-prefix') || 'Sort';
      var inp = sectionEl.querySelector('input[name^="artists_sort_by"]:checked');
      var optLabel = 'Most relevant';
      if (inp) {
        var wrap = inp.closest('label');
        var span = wrap && wrap.querySelector('.filter-item__label');
        if (span) optLabel = span.textContent.trim();
      }
      sortLabelEl.textContent = prefix + ': ' + optLabel;
    }

    function sortArtistsList(list) {
      var sortBy = getSortValue();
      var desc = sortBy === 'title-descending';
      list.sort(function (a, b) {
        var cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        return desc ? -cmp : cmp;
      });
    }

    function hasActiveFilters() {
      if (activeLetter !== 'all') return true;
      if (selectedFacetValues('category').length) return true;
      if (selectedFacetValues('medium').length) return true;
      if (selectedFacetValues('nationality').length) return true;
      if (exEl && exEl.checked) return true;
      if (worksEl && worksEl.checked) return true;
      return false;
    }

    function updateFiltersActiveClass() {
      if (!filterBarForm) return;
      filterBarForm.classList.toggle('filters-active', hasActiveFilters());
    }

    function syncSidebarToggleText() {
      if (!sidebarToggleBtn || !sidebarForm) return;
      var expanded = sidebarForm.getAttribute('aria-expanded') !== 'false';
      var textEl = sidebarToggleBtn.querySelector('.filter-bar__button-text');
      var ex = sidebarToggleBtn.dataset.expandedTitle || 'Hide filters';
      var col = sidebarToggleBtn.dataset.collapsedTitle || 'Filter';
      if (textEl) textEl.textContent = expanded ? ex : col;
    }

    function setSortDropdownOpen(open) {
      if (!sortToggleBtn || !sortDropdownEl || !filterBarForm) return;
      sortToggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      sortDropdownEl.setAttribute('aria-hidden', open ? 'false' : 'true');
      filterBarForm.classList.toggle('filter-bar--wash-active', open);
      var sortGroup = sortToggleBtn.closest('[data-filter-group]');
      if (sortGroup) sortGroup.classList.toggle('filter-group--active', open);
    }

    function mountFiltersForBreakpoint() {
      if (filterSidebarInner && sidebarForm && drawerFiltersInner) {
        if (mqMobile.matches) {
          if (filterSidebarInner.parentElement !== drawerFiltersInner) {
            drawerFiltersInner.appendChild(filterSidebarInner);
          }
        } else if (filterSidebarInner.parentElement !== sidebarForm) {
          sidebarForm.appendChild(filterSidebarInner);
        }
      }

      if (!mqMobile.matches) {
        closeArtistsFilterDrawer();
        setSortDropdownOpen(false);
      }
    }

    function closeArtistsFilterDrawer() {
      if (!filterDrawerEl) return;
      var flyouts = filterDrawerEl.querySelectorAll('[data-filter-modal]');
      flyouts.forEach(function (panel) {
        panel.classList.remove('active', 'animation--filter-bar-revealed');
        panel.setAttribute('aria-hidden', 'true');
      });
      filterDrawerEl.classList.remove('active');
      document.body.removeAttribute('data-fluorescent-overlay-open');
      document.body.style.overflow = '';
      if (drawerCloseTimer) clearTimeout(drawerCloseTimer);
      drawerCloseTimer = setTimeout(function () {
        filterDrawerEl.classList.remove('is-fixed');
        drawerCloseTimer = null;
      }, DRAWER_TRANSITION_MS);
    }

    function openArtistsFilterDrawer(target) {
      if (!filterDrawerEl || !target) return;
      var modal = filterDrawerEl.querySelector('[data-filter-modal="' + target + '"]');
      if (!modal) return;

      if (drawerCloseTimer) {
        clearTimeout(drawerCloseTimer);
        drawerCloseTimer = null;
      }

      filterDrawerEl.querySelectorAll('[data-filter-modal]').forEach(function (panel) {
        if (panel !== modal) {
          panel.classList.remove('active', 'animation--filter-bar-revealed');
          panel.setAttribute('aria-hidden', 'true');
        }
      });

      filterDrawerEl.classList.add('is-fixed');
      document.body.setAttribute('data-fluorescent-overlay-open', 'true');
      document.body.style.overflow = 'hidden';

      setTimeout(function () {
        if (shouldAnimateDrawer()) {
          modal.classList.add('animation--filter-bar-revealed');
        }
        filterDrawerEl.classList.add('active');
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');
      }, 0);
    }

    function initArtistsFilterDrawer() {
      if (!filterDrawerEl) return;

      var drawerWash = filterDrawerEl.querySelector('[data-drawer-wash]');
      var closeTargets = filterDrawerEl.querySelectorAll('[data-close-icon], [data-button="close"]');

      sectionEl.addEventListener(
        'click',
        function (e) {
          var trigger = e.target.closest('[data-filter-drawer-target]');
          if (!trigger || !app.contains(trigger)) return;
          e.preventDefault();
          var target = trigger.getAttribute('data-filter-drawer-target');
          if (!target) return;
          setSortDropdownOpen(false);
          openArtistsFilterDrawer(target);
        },
        { signal: signal }
      );

      if (drawerWash) {
        drawerWash.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            closeArtistsFilterDrawer();
          },
          { signal: signal }
        );
      }

      closeTargets.forEach(function (btn) {
        btn.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            closeArtistsFilterDrawer();
          },
          { signal: signal }
        );
      });

      filterDrawerEl.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'Escape' || e.keyCode === 27) closeArtistsFilterDrawer();
        },
        { signal: signal }
      );

      filterDrawerEl.addEventListener(
        'change',
        function (e) {
          var t = e.target;
          if (t && t.matches && t.matches('input[name^="artists_sort_by"]')) {
            updateSortLabel();
            applyFilters();
          }
        },
        { signal: signal }
      );

      var drawerClearAll = filterDrawerEl.querySelector('[data-artists-drawer-clear-all]');
      var drawerClearFilters = filterDrawerEl.querySelector('[data-artists-drawer-clear-filters]');
      function clearAllFilters() {
        if (clearBtn) clearBtn.click();
      }
      if (drawerClearAll) {
        drawerClearAll.addEventListener('click', clearAllFilters, { signal: signal });
      }
      if (drawerClearFilters) {
        drawerClearFilters.addEventListener('click', clearAllFilters, { signal: signal });
      }
    }

    function bindDrawerToggles(root) {
      if (!root) return;
      root.addEventListener(
        'click',
        function (e) {
          var btn = e.target.closest('[data-button="group_toggle"]');
          if (!btn || !root.contains(btn)) return;
          e.preventDefault();
          var panelId = btn.getAttribute('aria-controls');
          if (!panelId) return;
          var panel = document.getElementById(panelId);
          if (!panel || !root.contains(panel)) return;
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.setAttribute('aria-hidden', open ? 'true' : 'false');
        },
        { signal: signal }
      );
    }

    function buildLetters() {
      if (!lettersEl) return;
      lettersEl.innerHTML = '';

      function addLetter(val, label) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'artists-index__letter' + (val === 'all' ? ' is-active' : '');
        btn.textContent = label;
        btn.dataset.letter = val;
        btn.setAttribute('aria-pressed', val === 'all' ? 'true' : 'false');
        lettersEl.appendChild(btn);
      }

      addLetter('all', 'All');
      for (var i = 65; i <= 90; i += 1) {
        addLetter(String.fromCharCode(i), String.fromCharCode(i));
      }
      addLetter('#', '#');
    }

    function applyFilters() {
      var cats = selectedFacetValues('category');
      var meds = selectedFacetValues('medium');
      var nats = selectedFacetValues('nationality');
      var needEx = exEl && exEl.checked;
      var needWorks = worksEl && worksEl.checked;

      filtered = allArtists.filter(function (a) {
        if (activeLetter !== 'all' && a.letter !== activeLetter) return false;
        if (needEx && !a.exhibition) return false;
        if (needWorks && !a.works_available) return false;
        if (cats.length) {
          var hitC = cats.some(function (c) {
            return a.category.indexOf(c) !== -1;
          });
          if (!hitC) return false;
        }
        if (meds.length) {
          var hitM = meds.some(function (m) {
            return a.medium.indexOf(m) !== -1;
          });
          if (!hitM) return false;
        }
        if (nats.length) {
          var hitN = nats.some(function (n) {
            return a.nationality.indexOf(n) !== -1;
          });
          if (!hitN) return false;
        }
        return true;
      });

      sortArtistsList(filtered);
      visibleCount = pageSize;
      render();
      updateFiltersActiveClass();
    }

    function viewingRangeText(total, end) {
      var start = total > 0 ? 1 : 0;
      var ofRange = start + '-' + end;
      return viewingTemplate
        .replace(/\{\{\s*of\s*\}\}/g, ofRange)
        .replace(/\{\{\s*total\s*\}\}/g, String(total))
        .trim();
    }

    function updatePaginationUi(total, shown) {
      if (!paginationWrapEl) return;
      if (!total) {
        paginationWrapEl.hidden = true;
        if (loadMoreWrapEl) loadMoreWrapEl.hidden = true;
        return;
      }
      paginationWrapEl.hidden = false;
      if (paginationCountEl) {
        paginationCountEl.textContent = viewingRangeText(total, shown) + ' artists';
      }
      if (loadMoreWrapEl) {
        loadMoreWrapEl.hidden = shown >= total;
      }
    }

    function render() {
      var total = filtered.length;
      var shown = Math.min(visibleCount, total);
      var page = filtered.slice(0, shown);

      var countText;
      if (!total) {
        countText = '0 Results';
      } else if (total === 1) {
        countText = '1 Result';
      } else {
        countText = total + ' Results';
      }
      if (countEl) countEl.textContent = countText;
      if (drawerResultsCountEl) drawerResultsCountEl.textContent = countText;

      updatePaginationUi(total, shown);

      if (gridEl) {
        gridEl.innerHTML = page
          .map(function (a) {
            var href = escAttr(a.collectionUrl);
            var nameEsc = escHtml(a.name);
            var hasHover = Boolean(a.hoverUrl);
            var cardClass = 'artists-index__card' + (hasHover ? ' artists-index__card--has-hover' : '');

            var primary = a.portraitUrl
              ? '<img class="artists-index__card-img artists-index__card-img--primary" src="' +
                escAttr(a.portraitUrl) +
                '" alt="' +
                escAttr(a.portraitAlt || a.name) +
                '" loading="lazy" width="600" height="800" sizes="(min-width: 960px) 200px, 45vw">'
              : '<div class="artists-index__card-img artists-index__card-img--placeholder artists-index__card-img--primary" aria-hidden="true"></div>';

            var hoverImg = '';
            if (hasHover) {
              hoverImg =
                '<img class="artists-index__card-img artists-index__card-img--hover" src="' +
                escAttr(a.hoverUrl) +
                '" alt="" loading="lazy" width="600" height="800" sizes="(min-width: 960px) 200px, 45vw" aria-hidden="true">';
            }

            return (
              '<div class="' +
              cardClass +
              '" role="listitem">' +
              '<div class="artists-index__card-media">' +
              primary +
              hoverImg +
              '</div>' +
              '<a class="artists-index__card-name ff-heading fs-heading-6-base" href="' +
              href +
              '">' +
              nameEsc +
              '</a></div>'
            );
          })
          .join('');
      }
    }

    function bind() {
      if (lettersEl) {
        lettersEl.addEventListener(
          'click',
          function (e) {
            var t = e.target.closest('[data-letter]');
            if (!t) return;
            activeLetter = t.getAttribute('data-letter') || 'all';
            lettersEl.querySelectorAll('.artists-index__letter').forEach(function (b) {
              var on = b === t;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            applyFilters();
          },
          { signal: signal }
        );
      }

      app.addEventListener(
        'change',
        function (e) {
          var t = e.target;
          if (!t) return;
          if (t.matches && t.matches('[data-artists-facet], [data-filter-exhibition], [data-filter-works]')) {
            applyFilters();
          }
        },
        { signal: signal }
      );
      bindDrawerToggles(app);

      sectionEl.addEventListener(
        'change',
        function (e) {
          var t = e.target;
          if (t && t.matches && t.matches('input[name^="artists_sort_by"]')) {
            updateSortLabel();
            applyFilters();
          }
        },
        { signal: signal }
      );

      if (sidebarToggleBtn && sidebarForm) {
        syncSidebarToggleText();
        sidebarToggleBtn.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            var exp = sidebarForm.getAttribute('aria-expanded') !== 'false';
            sidebarForm.setAttribute('aria-expanded', exp ? 'false' : 'true');
            syncSidebarToggleText();
          },
          { signal: signal }
        );
      }

      if (sortToggleBtn && sortDropdownEl) {
        sortToggleBtn.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            if (mqMobile.matches) {
              closeArtistsFilterDrawer();
              openArtistsFilterDrawer('__sort');
              return;
            }
            var open = sortToggleBtn.getAttribute('aria-expanded') !== 'true';
            setSortDropdownOpen(open);
          },
          { signal: signal }
        );
      }

      app.addEventListener(
        'click',
        function (e) {
          if (!sortToggleBtn || !sortDropdownEl) return;
          if (sortToggleBtn.getAttribute('aria-expanded') !== 'true') return;
          var mf = e.target.closest('[data-mobile-filters]');
          if (mf && app.contains(mf)) return;
          var g = sortToggleBtn.closest('[data-filter-group]');
          if (g && g.contains(e.target)) return;
          setSortDropdownOpen(false);
        },
        { signal: signal }
      );

      var wash = sectionEl.querySelector('[data-artists-filter-bar-wash]');
      if (wash && filterBarForm) {
        wash.addEventListener(
          'click',
          function () {
            setSortDropdownOpen(false);
          },
          { signal: signal }
        );
      }

      initArtistsFilterDrawer();
      mountFiltersForBreakpoint();
      if (mqMobile.addEventListener) {
        mqMobile.addEventListener('change', mountFiltersForBreakpoint, { signal: signal });
      } else if (mqMobile.addListener) {
        mqMobile.addListener(mountFiltersForBreakpoint);
      }

      if (loadMoreBtn) {
        loadMoreBtn.addEventListener(
          'click',
          function () {
            visibleCount += pageSize;
            render();
          },
          { signal: signal }
        );
      }

      if (clearBtn) {
        clearBtn.addEventListener(
          'click',
          function () {
            activeLetter = 'all';
            if (lettersEl) {
              lettersEl.querySelectorAll('.artists-index__letter').forEach(function (b) {
                var on = b.getAttribute('data-letter') === 'all';
                b.classList.toggle('is-active', on);
                b.setAttribute('aria-pressed', on ? 'true' : 'false');
              });
            }
            app.querySelectorAll('input[data-artists-facet]:not(:disabled)').forEach(function (inp) {
              inp.checked = false;
            });
            if (exEl) exEl.checked = false;
            if (worksEl) worksEl.checked = false;
            applyFilters();
          },
          { signal: signal }
        );
      }

      updateSortLabel();
    }

    buildLetters();
    bind();

    loadAll()
      .then(function (rows) {
        allArtists = rows;
        var catSet = [];
        var medSet = [];
        var natSet = [];
        allArtists.forEach(function (a) {
          catSet = catSet.concat(a.category);
          medSet = medSet.concat(a.medium);
          natSet = natSet.concat(a.nationality);
        });
        fillFacetList(catListEl, 'category', uniqSort(catSet), allArtists);
        fillFacetList(medListEl, 'medium', uniqSort(medSet), allArtists);
        fillFacetList(natListEl, 'nationality', uniqSort(natSet), allArtists);
        applyFilters();
        showMain();
      })
      .catch(function (err) {
        showError(err && err.message ? err.message : 'Could not load artists.');
      });
  };
})();
